import type { CheckoutPageProps } from '@eventbrite/datalayer-library';
import { track, updatePageProps } from '@eventbrite/datalayer-library';
import { getWindowObject } from '@eventbrite/feature-detection';
import { LOCATION_CHANGE } from 'react-router-redux';
import { AnyAction, Dispatch, Store } from 'redux';
import { actionTypes } from 'redux-form';
import { HeapEventName } from '../constants/heap';
import {
    getBackendSource,
    getCheckoutAppVersion,
    getIsAppRebrandActive,
    getRebrandingStatus,
    getSeriesInfiniteCalendarFF,
} from '../selectors/app';
import { getIsEmbeddedInOrganizerSite } from '../selectors/app/getIsEmbeddedInOrganizerSite';
import { isExpressApplePayEnabled } from '../selectors/applePay';
import {
    getCategory,
    getEventId,
    getIsFreeEvent,
    getSubcategory,
} from '../selectors/event';
import { getSeriesSelectionType } from '../selectors/event/getSeriesSelectionType';
import { getCalendarType } from '../selectors/infiniteCalendarSeries/infiniteCalendarSeries';
import {
    getIsNosSourceOfTruth,
    getOrderId,
} from '../selectors/order/typedOrderSelectors';
import { getSelectedPaymentMethod } from '../selectors/payment';
import { isExpressPaypalEnabled } from '../selectors/paypal';
import { getIsTimedEntry, getSeriesId } from '../selectors/series';
import {
    getNumberOfAddonTicketsSelected,
    getNumberOfDonationTicketsSelected,
    getNumberOfFreeTicketsSelected,
    getNumberOfPaidTicketsSelected,
    getNumberOfTicketsSelected,
} from '../selectors/tickets/selected';
import { CheckoutState } from '../state';
import { getNativeAppVersion } from '../utils/browser';

const getCheckoutPageProps = (
    state: CheckoutState,
    payload: any,
): CheckoutPageProps & {
    category?: string;
    calendarType?: string;
    subcategory?: string;
    isSeriesInfiniteCalendar: boolean;
} => {
    const appVersion = getNativeAppVersion();
    const backendSource = getBackendSource(state);
    const category = getCategory(state);
    const checkoutVersion = getCheckoutAppVersion(state);
    const location = getWindowObject('location');
    const destinationURL = (location?.origin || '') + (payload?.pathname || '');
    const eventId = getEventId(state);
    const isNOS = getIsNosSourceOfTruth(state);
    const isFree = getIsFreeEvent(state) as boolean | undefined;
    const seriesId = getSeriesId(state);
    const orderId = getOrderId(state) || null;
    const totalTicketsSelected = getNumberOfTicketsSelected(state);
    const donationTicketsSelected = getNumberOfDonationTicketsSelected(state);
    const freeTicketsSelected = getNumberOfFreeTicketsSelected(state);
    const paidTicketsSelected = getNumberOfPaidTicketsSelected(state);
    const addonTicketsSelected = getNumberOfAddonTicketsSelected(state);
    const isExpressApplePayAvailable = isExpressApplePayEnabled(state);
    const isExpressPayPalAvailable = isExpressPaypalEnabled(state);
    const isEmbeddedInOrganizerSite = getIsEmbeddedInOrganizerSite(state);
    const seriesSelectionType = getSeriesSelectionType(state);
    const subcategory = getSubcategory(state);
    const isAppRebrandActive = getIsAppRebrandActive(state);
    const rebrandingStatus = getRebrandingStatus(state);
    // This prop will be true only for events displayed with the calendar on the listings page. For instance
    // selections made during checkout, a different prop is used, and in that case, this prop will be false.
    const timedEntry = getIsTimedEntry(state);
    const isSeriesInfiniteCalendar = getSeriesInfiniteCalendarFF(state);
    const calendarType = getCalendarType(state);
    return {
        addonTicketsSelected,
        appVersion,
        backendSource,
        category: category?.title,
        checkoutVersion,
        destinationURL,
        donationTicketsSelected,
        eventId,
        freeTicketsSelected,
        pathname: payload?.pathname,
        isEmbeddedInOrganizerSite,
        isExpressApplePayAvailable,
        isExpressPayPalAvailable,
        isFree,
        isNOS,
        orderId,
        paidTicketsSelected,
        rebranding_enabled: isAppRebrandActive,
        rebranding_status: rebrandingStatus,
        routerPayload: JSON.stringify(payload || {}),
        seriesId,
        subcategory: subcategory?.title,
        timedEntry,
        totalTicketsSelected,
        ...seriesSelectionType,
        isSeriesInfiniteCalendar,
        calendarType,
    };
};

export const dataLayerMiddleware =
    (store: Store<CheckoutState>) =>
    (next: Dispatch<CheckoutState>) =>
    (action: AnyAction) => {
        const state = store.getState();
        const { type, payload } = action;
        const location = getWindowObject('location');

        // This tracks form submits, i.e. passing from one step to another
        if (type == actionTypes.START_SUBMIT && action.meta?.form) {
            track({
                eventName: HeapEventName.submit,
                eventData: {
                    category: getCategory(state)?.title,
                    form: action.meta.form,
                    orderId: getOrderId(state),
                    rebranding_enabled: getIsAppRebrandActive(state),
                    rebranding_status: getRebrandingStatus(state),
                    selectedPaymentMethod: getSelectedPaymentMethod(state),
                    subcategory: getSubcategory(state)?.title,
                },
            });
        }

        // This enriches all following events with all the data we're interested in
        if (
            type == LOCATION_CHANGE &&
            payload?.pathname &&
            payload.pathname !== '/' &&
            location
        ) {
            // Record Navigation Change Event w/ New Page Props.
            updatePageProps({
                page: getCheckoutPageProps(state, payload),
                eventName: HeapEventName.routerLocationChange,
            });
        }

        return next(action);
    };
